import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import approvalService from '../../services/approvalService';
import './Approvals.css';
import CommonModal from '../../services/CommonModal';

const Approvals = () => {
  const navigate = useNavigate();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedApproval, setSelectedApproval] = useState(null);
    const confirmDelete = () => {
    handleDelete(selectedApproval);
    setShowDeleteModal(false);
    setSelectedApproval(null);
  };
  const queryClient = useQueryClient();
  const [filters, setFilters] = useState({
    status: '',
    approval_type: '',
    priority: ''
  });

  const { data, isLoading } = useQuery({
    queryKey: ['approvals', filters],
    queryFn: () => approvalService.getAllApprovals(filters)
  });

  const approvals = data?.approvals || [];

  const deleteMutation = useMutation({
    mutationFn: approvalService.deleteApproval,
    onSuccess: () => {
      queryClient.invalidateQueries(['approvals']);
      toast.success('Approval deleted successfully');
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || 'Failed to delete approval');
    }
  });

  const approveMutation = useMutation({
    mutationFn: ({ id, data }) => approvalService.approveApproval(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries(['approvals']);
      toast.success('Approval approved successfully');
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || 'Failed to approve');
    }
  });

  const rejectMutation = useMutation({
    mutationFn: ({ id, data }) => approvalService.rejectApproval(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries(['approvals']);
      toast.success('Approval rejected successfully');
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || 'Failed to reject');
    }
  });

  const handleDelete = (id) => {
     {
      deleteMutation.mutate(id);
    }
  };

  const handleApprove = (id) => {
    const notes = prompt('Enter approval notes (optional):');
    approveMutation.mutate({ id, data: { approval_notes: notes || '' } });
  };

  const handleReject = (id) => {
    const reason = prompt('Enter rejection reason:');
    if (reason) {
      rejectMutation.mutate({ id, data: { rejection_reason: reason } });
    }
  };

  const getStatusBadgeClass = (status) => {
    const classes = {
      'Pending': 'badge-warning',
      'Approved': 'badge-success',
      'Rejected': 'badge-danger',
      'Cancelled': 'badge-secondary'
    };
    return classes[status] || 'badge-secondary';
  };

  const getPriorityBadgeClass = (priority) => {
    const classes = {
      'Urgent': 'badge-danger',
      'High': 'badge-warning',
      'Medium': 'badge-info',
      'Low': 'badge-secondary'
    };
    return classes[priority] || 'badge-secondary';
  };

  if (isLoading) {
    return (
      <div className="approvals-page">
        <div className="loading">Loading approvals...</div>
      </div>
    );
  }

  return (
    <div className="approvals-page">
      <div className="page-header">
        <h1>Approvals</h1>
        <button className="btn btn-primary" onClick={() => navigate('/app/approvals/new')}>
          <i className="fas fa-plus"></i> New Approval Request
        </button>
      </div>

      <div className="filters-section">
        <div className="filter-group">
          <label>Status:</label>
          <select
            value={filters.status}
            onChange={(e) => setFilters({ ...filters, status: e.target.value })}
          >
            <option value="">All</option>
            <option value="Pending">Pending</option>
            <option value="Approved">Approved</option>
            <option value="Rejected">Rejected</option>
            <option value="Cancelled">Cancelled</option>
          </select>
        </div>

        <div className="filter-group">
          <label>Type:</label>
          <select
            value={filters.approval_type}
            onChange={(e) => setFilters({ ...filters, approval_type: e.target.value })}
          >
            <option value="">All</option>
            <option value="Budget">Budget</option>
            <option value="Change Request">Change Request</option>
            <option value="Document">Document</option>
            <option value="Expense">Expense</option>
            <option value="Project">Project</option>
            <option value="Resource">Resource</option>
            <option value="Risk">Risk</option>
            <option value="Task">Task</option>
            <option value="Time Off">Time Off</option>
            <option value="Other">Other</option>
          </select>
        </div>

        <div className="filter-group">
          <label>Priority:</label>
          <select
            value={filters.priority}
            onChange={(e) => setFilters({ ...filters, priority: e.target.value })}
          >
            <option value="">All</option>
            <option value="Urgent">Urgent</option>
            <option value="High">High</option>
            <option value="Medium">Medium</option>
            <option value="Low">Low</option>
          </select>
        </div>
      </div>

                 <CommonModal
        isOpen={showDeleteModal}
        title="Delete Approval"
        message="Are you sure you want to delete this approval?"
        onClose={() => {
          setShowDeleteModal(false);
          setSelectedApproval(null);
        }}
        onConfirm={confirmDelete}
        type="error"
        confirmText="Delete"
        cancelText="Cancel"
      />

      <div className="approvals-list">
        {approvals?.length === 0 ? (
          <div className="empty-state">
            <i className="fas fa-check-circle"></i>
            <p>No approvals found</p>
          </div>
        ) : (
          <div className="table-responsive">
            <table className="table">
              <thead>
                <tr>
                  <th>Title</th>
                  <th>Type</th>
                  <th>Priority</th>
                  <th>Status</th>
                  <th>Requested By</th>
                  <th>Approver</th>
                  <th>Due Date</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {approvals?.map((approval) => (
                  <tr key={approval.id}>
                    <td>
                      <strong>{approval.title}</strong>
                      {approval.is_urgent && (
                        <span className="badge badge-danger ml-2">URGENT</span>
                      )}
                    </td>
                    <td>{approval.approval_type}</td>
                    <td>
                      <span className={`badge ${getPriorityBadgeClass(approval.priority)}`}>
                        {approval.priority}
                      </span>
                    </td>
                    <td>
                      <span className={`badge ${getStatusBadgeClass(approval.status)}`}>
                        {approval.status}
                      </span>
                    </td>
                    <td>{approval.requested_by_name || 'N/A'}</td>
                    <td>{approval.approver_name || 'N/A'}</td>
                    <td>{approval.due_date ? new Date(approval.due_date).toLocaleDateString() : 'N/A'}</td>
                    <td>
                      <div className="action-buttons">
                        <button
                          className="btn btn-sm btn-info"
                          onClick={() => navigate(`/app/approvals/${approval.id}`)}
                          title="View"
                        >
                          <i className="fas fa-eye"></i>
                        </button>
                        {approval.status === 'Pending' && (
                          <>
                            <button
                              className="btn btn-sm btn-success"
                              onClick={() => handleApprove(approval.id)}
                              title="Approve"
                            >
                              <i className="fas fa-check"></i>
                            </button>
                            <button
                              className="btn btn-sm btn-danger"
                              onClick={() => handleReject(approval.id)}
                              title="Reject"
                            >
                              <i className="fas fa-times"></i>
                            </button>
                          </>
                        )}
                        <button
                          className="btn btn-sm btn-danger"
                          onClick={() => {
    setSelectedApproval(approval.id);
    setShowDeleteModal(true);}}
                          title="Delete"
                        >
                          <i className="fas fa-trash"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default Approvals;


